import { createLogger } from './logger';

const log = createLogger('RateLimiter');

/**
 * Token-bucket rate limiter.
 *
 * acquire() resolves when a token is available. Tokens refill at
 * `tokensPerSecond` up to `bucketSize`. Refill is calculated lazily on each
 * acquire() call using elapsed wall-clock time — no setInterval drift.
 *
 * Inject a `clock` function in tests to control time without real timers.
 */
export class RateLimiter {
  private readonly tokensPerSecond: number;
  private readonly bucketSize: number;
  private readonly clock: () => number;
  private tokens: number;
  private lastRefillAt: number;

  constructor(tokensPerSecond: number, bucketSize: number, clock: () => number = Date.now) {
    this.tokensPerSecond = tokensPerSecond;
    this.bucketSize = bucketSize;
    this.clock = clock;
    this.tokens = bucketSize; // start full
    this.lastRefillAt = clock();
  }

  async acquire(): Promise<void> {
    this._refill();

    if (this.tokens >= 1) {
      this.tokens -= 1;
      return;
    }

    // Wait until one token is available, then recurse (handles concurrent callers)
    const waitMs = Math.ceil(((1 - this.tokens) / this.tokensPerSecond) * 1000);
    log.debug(`RateLimiter waiting ${waitMs.toString()}ms`);
    await new Promise<void>((resolve) => setTimeout(resolve, waitMs));
    return this.acquire();
  }

  private _refill(): void {
    const now = this.clock();
    const elapsed = now - this.lastRefillAt;
    const newTokens = (elapsed / 1000) * this.tokensPerSecond;
    this.tokens = Math.min(this.bucketSize, this.tokens + newTokens);
    this.lastRefillAt = now;
  }
}
